import { useState, useEffect } from 'react';
import { X, Zap, Calendar, Clock, CheckCircle, RefreshCw } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import DatePicker from './DatePicker';
import { useModal } from './AppModal';
import { generateRecurringInstances } from './recurringJobUtils';
import { useTheme } from './ThemeContext';

export default function PostSignatureWorkflow({ quote, companyId, onClose, onComplete }) {
  const { showAlert, showError } = useModal();
  const { colors } = useTheme();
  const [choice, setChoice] = useState(null); // 'now', 'schedule-now', 'schedule-later'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('9:00');
  const [estimatedDuration, setEstimatedDuration] = useState(60); // minutes
  const [creating, setCreating] = useState(false);

  // Recurring options
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceRule, setRecurrenceRule] = useState('weekly');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');

  // Fetch company settings for recurring
  useEffect(() => {
    const fetchRecurringEnabled = async () => {
      const { data } = await supabaseData
        .from('companies')
        .select('recurring_enabled')
        .eq('id', companyId || quote.company_id)
        .single();
      setRecurringEnabled(data?.recurring_enabled || false);
    };
    fetchRecurringEnabled();
  }, [companyId, quote.company_id]);

  // Generate time options for 24 hours in 15-minute increments
  const timeOptions = [];
  for (let hour = 0; hour <= 23; hour++) {
    timeOptions.push(`${hour}:00`);
    timeOptions.push(`${hour}:15`);
    timeOptions.push(`${hour}:30`);
    timeOptions.push(`${hour}:45`);
  }

  const generateJobNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const time = String(date.getTime()).slice(-6);
    return `JOB-${year}${month}${day}-${time}`;
  };

  const createJob = async (schedDate, schedTime) => {
    setCreating(true);
    try {
      const jobNumber = generateJobNumber();

      const jobData = {
          company_id: companyId || quote.company_id,
          job_number: jobNumber,
          customer_id: quote.customer_id,
          quote_id: quote.id,
          job_type: 'repair',
          scheduled_date: schedDate,
          scheduled_time: schedTime,
          estimated_duration: estimatedDuration,
          status: 'scheduled',
          quoted_value: quote.total,
          pricing_table_id: quote.pricing_table_id,
          deposit_required: quote.deposit_required || false,
          deposit_amount: quote.deposit_amount || 0,
          deposit_paid: quote.deposit_paid || false,
          deposit_paid_at: quote.deposit_paid ? (quote.deposit_paid_at || new Date().toISOString()) : null
        };

      // Add recurring fields if enabled
      if (isRecurring && recurrenceEndDate) {
        jobData.is_recurring = true;
        jobData.recurrence_rule = recurrenceRule;
        jobData.recurrence_end_date = recurrenceEndDate;
      }

      const { data: job, error } = await supabaseData
        .from('jobs')
        .insert([jobData])
        .select()
        .single();

      if (error) throw error;

      // Copy quote items to job_items
      const { data: quoteItems, error: itemsError } = await supabaseData
        .from('quote_items')
        .select('*')
        .eq('quote_id', quote.id);

      if (!itemsError && quoteItems && quoteItems.length > 0) {
        const jobItems = quoteItems.map(item => ({
          job_id: job.id,
          sku_id: item.sku_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          is_additional: false
        }));

        const { error: jobItemsError } = await supabaseData
          .from('job_items')
          .insert(jobItems);

        if (jobItemsError) {
          // Don't throw - job was created successfully
        }
      }

      // Update quote status to 'scheduled'
      const { error: quoteError } = await supabaseData
        .from('quotes')
        .update({ status: 'scheduled' })
        .eq('id', quote.id);

      if (quoteError) {
        // Don't throw - job was created successfully
      }

      // Generate recurring instances if this is a recurring job
      if (isRecurring && recurrenceEndDate && job) {
        const result = await generateRecurringInstances(job);
        if (!result.success) {
        }
      }

      onComplete(job);
      onClose();
    } catch (error) {
      // More helpful error message for network errors
      const errorMsg = error.message === 'load failed' || error.message === 'Failed to fetch'
        ? 'Network error - please check your connection and try again'
        : error.message;
      showError('Failed to create job: ' + errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const handleDoNow = () => {
    const now = new Date();
    const todayDate = now.toISOString().split('T')[0];
    const currentTime = `${now.getHours()}:${now.getMinutes().toString().padStart(2, '0')}`;
    createJob(todayDate, currentTime);
  };

  const handleScheduleNow = () => {
    setChoice('schedule-now');
  };

  const handleScheduleLater = () => {
    setChoice('schedule-later');
  };

  const handleConfirmSchedule = () => {
    if (choice === 'schedule-now') {
      // Schedule for today at selected time
      const today = new Date().toISOString().split('T')[0];
      createJob(today, scheduledTime);
    } else if (choice === 'schedule-later') {
      // Schedule for selected date and time
      if (!scheduledDate) {
        showAlert('Please select a date', { title: 'Date Required' });
        return;
      }
      createJob(scheduledDate, scheduledTime);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000,
      padding: '1rem'
    }}>
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        width: '100%',
        maxWidth: '600px',
        border: `1px solid ${colors.border}`,
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: colors.bgInput
        }}>
          <div>
            <h2 style={{ margin: 0, color: colors.text }}>
              ✅ Quote Signed!
            </h2>
            <p style={{ margin: '0.5rem 0 0 0', color: colors.textMuted, fontSize: '0.9rem' }}>
              What would you like to do next?
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          {!choice ? (
            // Choice Selection
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {/* Do Now */}
              <div
                onClick={handleDoNow}
                style={{
                  background: colors.accentGradient,
                  padding: '1.5rem',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  border: '3px solid transparent',
                  transition: 'all 0.2s',
                  color: '#fff',
                  textAlign: 'center'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(240, 90, 40, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Zap size={48} style={{ marginBottom: '0.5rem' }} />
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
                  Do This Job NOW
                </h3>
                <p style={{ margin: 0, fontSize: '0.95rem', opacity: 0.9 }}>
                  Start work immediately
                </p>
              </div>

              {/* Schedule Now (Today) */}
              <div
                onClick={handleScheduleNow}
                style={{
                  background: colors.bgInput,
                  padding: '1.5rem',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  border: `2px solid ${colors.border}`,
                  transition: 'all 0.2s',
                  textAlign: 'center'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.borderColor = '#4ecca3';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.borderColor = colors.border;
                }}
              >
                <Clock size={48} style={{ marginBottom: '0.5rem', color: '#4ecca3' }} />
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', color: colors.text }}>
                  Schedule for Today
                </h3>
                <p style={{ margin: 0, fontSize: '0.9rem', color: colors.textMuted }}>
                  Pick a time slot for today
                </p>
              </div>

              {/* Schedule Later */}
              <div
                onClick={handleScheduleLater}
                style={{
                  background: colors.bgInput,
                  padding: '1.5rem',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  border: `2px solid ${colors.border}`,
                  transition: 'all 0.2s',
                  textAlign: 'center'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.borderColor = '#45b7d1';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.borderColor = colors.border;
                }}
              >
                <Calendar size={48} style={{ marginBottom: '0.5rem', color: '#45b7d1' }} />
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', color: colors.text }}>
                  Schedule for Later
                </h3>
                <p style={{ margin: 0, fontSize: '0.9rem', color: colors.textMuted }}>
                  Pick a future date and time
                </p>
              </div>

              {/* Skip - No Job Yet */}
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: `1px solid ${colors.border}`,
                  padding: '1rem',
                  borderRadius: '8px',
                  color: colors.textMuted,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  marginTop: '1rem'
                }}
              >
                Skip - Don't Create Job Yet
              </button>
            </div>
          ) : (
            // Scheduling Form
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem'
            }}>
              {choice === 'schedule-now' && (
                <>
                  <div style={{
                    background: colors.bgInput,
                    padding: '1rem',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                    textAlign: 'center'
                  }}>
                    <Calendar size={24} style={{ color: '#4ecca3', marginBottom: '0.5rem' }} />
                    <div style={{ color: colors.text, fontSize: '1.1rem', fontWeight: 'bold' }}>
                      Today - {new Date().toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      color: colors.text,
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Clock size={18} />
                      Select Time
                    </label>
                    <select
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '1rem',
                        cursor: 'pointer'
                      }}
                    >
                      {timeOptions.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      color: colors.text,
                      fontWeight: 'bold'
                    }}>
                      Estimated Duration
                    </label>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '0.5rem'
                    }}>
                      {[
                        { label: '30 min', value: 30 },
                        { label: '1 hour', value: 60 },
                        { label: '1.5 hours', value: 90 },
                        { label: '2 hours', value: 120 },
                        { label: '3 hours', value: 180 },
                        { label: '4 hours', value: 240 }
                      ].map(duration => (
                        <button
                          key={duration.value}
                          type="button"
                          onClick={() => setEstimatedDuration(duration.value)}
                          style={{
                            padding: '0.75rem',
                            background: estimatedDuration === duration.value ? colors.accent : colors.bgInput,
                            border: estimatedDuration === duration.value ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: estimatedDuration === duration.value ? '#fff' : colors.text,
                            fontSize: '0.85rem',
                            fontWeight: estimatedDuration === duration.value ? 'bold' : 'normal',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          {duration.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {choice === 'schedule-later' && (
                <>
                  <DatePicker
                    label="Select Date"
                    value={scheduledDate}
                    onChange={setScheduledDate}
                    min={new Date().toISOString().split('T')[0]}
                    required={true}
                  />

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      color: colors.text,
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Clock size={18} />
                      Select Time
                    </label>
                    <select
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '1rem',
                        cursor: 'pointer'
                      }}
                    >
                      {timeOptions.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      color: colors.text,
                      fontWeight: 'bold'
                    }}>
                      Estimated Duration
                    </label>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '0.5rem'
                    }}>
                      {[
                        { label: '30 min', value: 30 },
                        { label: '1 hour', value: 60 },
                        { label: '1.5 hours', value: 90 },
                        { label: '2 hours', value: 120 },
                        { label: '3 hours', value: 180 },
                        { label: '4 hours', value: 240 }
                      ].map(duration => (
                        <button
                          key={duration.value}
                          type="button"
                          onClick={() => setEstimatedDuration(duration.value)}
                          style={{
                            padding: '0.75rem',
                            background: estimatedDuration === duration.value ? colors.accent : colors.bgInput,
                            border: estimatedDuration === duration.value ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: estimatedDuration === duration.value ? '#fff' : colors.text,
                            fontSize: '0.85rem',
                            fontWeight: estimatedDuration === duration.value ? 'bold' : 'normal',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          {duration.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Recurring Options - Only show when scheduling */}
              {recurringEnabled && (choice === 'schedule-now' || choice === 'schedule-later') && (
                <div style={{
                  background: colors.bgInput,
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #3a3a3a'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    marginBottom: isRecurring ? '1rem' : 0
                  }}>
                    <input
                      type="checkbox"
                      checked={isRecurring}
                      onChange={(e) => setIsRecurring(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <RefreshCw size={18} style={{ color: '#4ecca3' }} />
                    <span style={{ color: colors.text, fontWeight: 'bold' }}>Make This a Recurring Job</span>
                  </label>

                  {isRecurring && (
                    <>
                      {/* Recurrence Pattern */}
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                          Repeat Every
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {[
                            { label: 'Weekly', value: 'weekly' },
                            { label: 'Bi-Weekly', value: 'biweekly' },
                            { label: 'Monthly', value: 'monthly' }
                          ].map(option => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setRecurrenceRule(option.value)}
                              style={{
                                flex: 1,
                                padding: '0.6rem',
                                background: recurrenceRule === option.value ? '#4ecca3' : colors.bgInput,
                                border: recurrenceRule === option.value ? '2px solid #4ecca3' : `1px solid ${colors.border}`,
                                borderRadius: '6px',
                                color: recurrenceRule === option.value ? '#000' : colors.text,
                                fontSize: '0.85rem',
                                fontWeight: recurrenceRule === option.value ? 'bold' : 'normal',
                                cursor: 'pointer'
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* End Date */}
                      <DatePicker
                        label="Repeat Until"
                        value={recurrenceEndDate}
                        onChange={setRecurrenceEndDate}
                        min={scheduledDate || new Date().toISOString().split('T')[0]}
                        required={isRecurring}
                      />

                      {recurrenceEndDate && (
                        <div style={{
                          marginTop: '0.75rem',
                          padding: '0.5rem',
                          background: '#4ecca320',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          color: '#4ecca3'
                        }}>
                          <RefreshCw size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
                          {recurrenceRule === 'weekly' ? 'Weekly' : recurrenceRule === 'biweekly' ? 'Every 2 weeks' : 'Monthly'} until {new Date(recurrenceEndDate).toLocaleDateString()}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '1rem',
                marginTop: '1rem'
              }}>
                <button
                  onClick={handleConfirmSchedule}
                  disabled={creating || (choice === 'schedule-later' && !scheduledDate)}
                  style={{
                    flex: 1,
                    background: (!creating && (choice === 'schedule-now' || scheduledDate)) ? '#4ecca3' : colors.bgHover,
                    color: (!creating && (choice === 'schedule-now' || scheduledDate)) ? '#000' : colors.textMuted,
                    border: 'none',
                    padding: '1rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    cursor: (!creating && (choice === 'schedule-now' || scheduledDate)) ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {creating ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(0,0,0,0.3)',
                        borderTop: '2px solid #000',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Creating Job...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      Create Job
                    </>
                  )}
                </button>

                <button
                  onClick={() => setChoice(null)}
                  disabled={creating}
                  style={{
                    flex: 1,
                    background: colors.bgInput,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    padding: '1rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: creating ? 'not-allowed' : 'pointer'
                  }}
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
